import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import {
    addItemToDB,
    getAllItemsFromDB,
    removeItemFromDB,
    clearAllFromDB,
    updateItemFromDB,
} from "../db/db";
import { formatJsonText } from "../component/formatters/format_string";

const ItemsContext = createContext();
export const useItems = () => useContext(ItemsContext);

const normalizeText = (text) => {
    if (typeof text === "string") return text;
    if (text && typeof text === "object" && Array.isArray(text.data)) {
        try { return new TextDecoder().decode(new Uint8Array(text.data)); } catch { }
    }
    return "";
};

export const ItemsProvider = ({ children }) => {
    const [items, setItems] = useState([]);
    const dbTimer = useRef(null);

    const loadItems = useCallback(async () => {
        const all = await getAllItemsFromDB();
        if (all.length > 0) {
            setItems(all.map((item, index, arr) => ({
                ...item,
                text: normalizeText(item.text),
                compareText: "",
                scrollPosition: 0,
                isActive: index === arr.length - 1,
            })));
        } else {
            createFirstItem();
        }
    }, []);

    useEffect(() => { loadItems(); }, [loadItems]);

    const activeItem = items.find(item => item.isActive === true) ?? null;

    // Always keep a ref in sync so debounced DB writes can access latest item
    const activeItemRef = useRef(activeItem);
    activeItemRef.current = activeItem;

    // -----------------------------------
    // JSON Validation
    // -----------------------------------
    const getJsonValidation = () => {
        const text = activeItem?.text?.trim();
        if (!text) return { isValid: null, error: null };
        try {
            JSON.parse(text);
            return { isValid: true, error: null };
        } catch (e) {
            return { isValid: false, error: e.message };
        }
    };

    // -----------------------------------
    // Clipboard
    // -----------------------------------
    const handleCopy = async () => {
        if (!activeItem?.text) return;
        await navigator.clipboard.writeText(activeItem.text);
    };

    const handlePaste = async () => {
        try {
            const text = await navigator.clipboard.readText();
            updateItemData(text);
        } catch { }
    };

    // -----------------------------------
    // Stats
    // -----------------------------------
    const analyzeString = () => {
        if (!activeItem) return { lines: 0, words: 0, characters: 0, bytes: "0 B" };
        const str = activeItem.text ?? "";
        const lines = str === "" ? 1 : str.split(/\r\n|\r|\n/).length;
        const words = str.trim() === "" ? 0 : str.trim().split(/\s+/).filter(Boolean).length;
        const characters = str.length;
        const encoder = new TextEncoder();
        let byteCount = encoder.encode(str).length;
        let bytes;
        if (byteCount < 1024) bytes = byteCount + " B";
        else if (byteCount < 1024 * 1024) bytes = (byteCount / 1024).toFixed(2) + " KB";
        else bytes = (byteCount / (1024 * 1024)).toFixed(2) + " MB";
        return { lines, words, characters, bytes };
    };

    // -----------------------------------
    // CRUD
    // -----------------------------------
    const createFirstItem = () => {
        const file = { id: Date.now(), name: "untitled.json", text: "", compareText: "", scrollPosition: 0, isActive: true };
        setItems([file]);
        addItemToDB(file);
    };

    const addNewItem = () => {
        const file = { id: Date.now(), name: "untitled.json", text: "", compareText: "", scrollPosition: 0, isActive: true };
        setItems(prev => prev.map(i => ({ ...i, isActive: false })).concat(file));
        addItemToDB(file);
    };

    const updateItemName = (id, name) => {
        setItems(prev => prev.map(item => item.id === id ? { ...item, name } : item));
        const item = items.find(i => i.id === id);
        if (item) updateItemFromDB({ ...item, name });
    };

    const updateItemData = (text) => {
        if (!activeItem) return;
        const id = activeItem.id;
        const snapshot = { ...activeItem, text };
        setItems(prev => prev.map(item => item.id === id ? { ...item, text } : item));
        if (dbTimer.current) clearTimeout(dbTimer.current);
        dbTimer.current = setTimeout(() => updateItemFromDB(snapshot), 500);
    };

    const updateCompareText = (compareText) => {
        if (!activeItem) return;
        const id = activeItem.id;
        setItems(prev => prev.map(item => item.id === id ? { ...item, compareText } : item));
    };

    const removeItem = (id) => {
        const remaining = items.filter(i => i.id !== id);
        removeItemFromDB(id);
        if (remaining.length === 0) {
            const file = { id: Date.now(), name: "untitled.json", text: "", isActive: true };
            setItems([file]);
            addItemToDB(file);
        } else {
            const nextActive = remaining[remaining.length - 1];
            setItems(remaining.map(item => ({ ...item, isActive: item.id === nextActive.id })));
        }
    };

    const clearAll = async () => {
        setItems([]);
        await clearAllFromDB();
        createFirstItem();
    };

    const selectItem = (id) => {
        setItems(prev => prev.map(item => ({ ...item, isActive: item.id === id })));
    };

    // -----------------------------------
    // JSON Transforms
    // -----------------------------------
    const beautifyJSON = () => {
        if (!activeItem) return;
        const formatted = formatJsonText(activeItem.text);
        updateItemData(formatted);
    };

    const minifyJSON = () => {
        if (!activeItem?.text?.trim()) return;
        try {
            updateItemData(JSON.stringify(JSON.parse(activeItem.text)));
        } catch { }
    };

    return (
        <ItemsContext.Provider value={{
            items,
            activeItem,
            addNewItem,
            updateItemName,
            updateItemData,
            updateCompareText,
            removeItem,
            clearAll,
            selectItem,
            handleCopy,
            handlePaste,
            beautifyJSON,
            minifyJSON,
            analyzeString,
            getJsonValidation,
        }}>
            {children}
        </ItemsContext.Provider>
    );
};
